import type { ReactNode } from "react";
import type { TranslationKey } from "../../lib/i18n";
import { useConfig } from "../../state/config-context";

type Tone = "positive" | "negative" | "muted";

const STATUS_KEYS: Record<string, TranslationKey> = {
  draft: "billing.status.draft",
  sent: "billing.status.sent",
  accepted: "billing.status.accepted",
  rejected: "billing.status.rejected",
  paid: "billing.status.paid",
  void: "billing.status.void",
  overdue: "billing.status.overdue",
  active: "billing.status.active",
  archived: "billing.status.archived",
  blocked: "billing.status.blocked",
  closed: "billing.status.closed",
};

const PRODUCT_KIND_KEYS: Record<string, TranslationKey> = {
  product: "billing.kind.product",
  service: "billing.kind.service",
};

const TAX_KIND_KEYS: Record<string, TranslationKey> = {
  ad_valorem: "billing.taxKind.adValorem",
  fixed: "billing.taxKind.fixed",
  withholding: "billing.taxKind.withholding",
};

const UNIT_KEYS: Record<string, TranslationKey> = {
  unit: "billing.unit.unit",
  each: "billing.unit.each",
  hour: "billing.unit.hour",
  day: "billing.unit.day",
  week: "billing.unit.week",
  month: "billing.unit.month",
  year: "billing.unit.year",
  service: "billing.unit.service",
};

const METHOD_KEYS: Record<string, TranslationKey> = {
  cash: "billing.method.cash",
  check: "billing.method.check",
  transfer: "billing.method.transfer",
  card: "billing.method.card",
  other: "billing.method.other",
  external_ref: "billing.method.externalRef",
};

function statusTone(status: string): Tone {
  if (status === "accepted" || status === "paid" || status === "active") {
    return "positive";
  }
  if (status === "rejected" || status === "overdue" || status === "void" || status === "blocked") {
    return "negative";
  }
  return "muted";
}

function Badge({ tone, label }: { tone: Tone; label: string }): ReactNode {
  return <span className={`badge badge--${tone}`}>{label}</span>;
}

export function BillingStatusBadge({ status }: { status: string }): ReactNode {
  const { t } = useConfig();
  const key = STATUS_KEYS[status];
  return <Badge tone={statusTone(status)} label={key === undefined ? status : t(key)} />;
}

export function BillingProductKindLabel({ kind }: { kind: string }): ReactNode {
  const { t } = useConfig();
  const key = PRODUCT_KIND_KEYS[kind];
  return <span>{key === undefined ? kind : t(key)}</span>;
}

export function BillingTaxKindLabel({ kind }: { kind: string }): ReactNode {
  const { t } = useConfig();
  const key = TAX_KIND_KEYS[kind];
  return <span>{key === undefined ? kind : t(key)}</span>;
}

export function BillingUnitLabel({ unit }: { unit: string }): ReactNode {
  const { t } = useConfig();
  const key = UNIT_KEYS[unit];
  return <span>{key === undefined ? unit : t(key)}</span>;
}

export function BillingMethodLabel({ method }: { method: string }): ReactNode {
  const { t } = useConfig();
  const key = METHOD_KEYS[method];
  return <span>{key === undefined ? method : t(key)}</span>;
}
